import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { computeFlowStatus, EMPTY_FLOW_DATA, FlowData, FlowStatus } from './flowStatus';
import { loadFlowData, LoadFlowDataResult } from './loadFlowData';
import { flowStepByPath } from './flowSteps';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

export interface LinkedInFlowValue {
  data: FlowData;
  status: FlowStatus;
  loading: boolean;
  /** Erster Ladefehler (Teilausfaelle werden geduldet). */
  error: string | null;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const LinkedInFlowContext = createContext<LinkedInFlowValue | null>(null);

type Loader = () => Promise<LoadFlowDataResult>;

function useFlowState(loader: Loader, autoLoad: boolean): LinkedInFlowValue {
  const [data, setData] = useState<FlowData>(EMPTY_FLOW_DATA);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    setLoading(true);
    const p = loader()
      .then((result) => {
        if (!mounted.current) return;
        setData(result.data);
        setError(result.errors[0] ?? null);
        setLoaded(true);
      })
      .catch((err: any) => {
        if (!mounted.current) return;
        setError(err?.message || 'Prozessstatus konnte nicht geladen werden');
        setLoaded(true);
      })
      .finally(() => {
        inFlight.current = null;
        if (mounted.current) setLoading(false);
      });
    inFlight.current = p;
    return p;
  }, [loader]);

  useEffect(() => {
    if (autoLoad && !loaded && !inFlight.current) {
      refresh();
    }
  }, [autoLoad, loaded, refresh]);

  const status = useMemo(() => computeFlowStatus(data), [data]);

  return useMemo(
    () => ({ data, status, loading, error, loaded, refresh }),
    [data, status, loading, error, loaded, refresh],
  );
}

/** Pfade, auf denen der Status ohne Aufforderung geladen wird. */
export function isFlowRoute(pathname: string): boolean {
  return pathname === '/' || pathname === '/gesamtprozess' || Boolean(flowStepByPath(pathname));
}

export interface LinkedInFlowProviderProps {
  children: React.ReactNode;
  /** Ersatz-Loader fuer Tests. */
  loader?: Loader;
  /** Ohne Angabe: laedt sobald eine LinkedIn-Route aktiv ist. Ohne linkedin.lesen nie. */
  autoLoad?: boolean;
}

/**
 * Ein Ladevorgang fuer alle LinkedIn-Seiten: die Shell haengt den Provider
 * ein, Seiten lesen ueber useLinkedInFlow() und stossen nach Aenderungen
 * refresh() an.
 *
 * Welle 2: die neun GET-Aufrufe aus loadFlowData liegen alle in der
 * LinkedIn-Zeile (linkedin.lesen). Ohne dieses Recht (Buchhaltung) laedt der
 * Provider nichts -- auch nicht auf '/', das als Prozess-Route zaehlt --,
 * statt neun 403 zu sammeln. Der Status bleibt dann leer (loaded false).
 */
export const LinkedInFlowProvider: React.FC<LinkedInFlowProviderProps> = ({ children, loader = loadFlowData, autoLoad }) => {
  const location = useLocation();
  const { darf } = useBerechtigung();
  const shouldLoad = darf('linkedin.lesen') && (autoLoad ?? isFlowRoute(location.pathname));
  const value = useFlowState(loader, shouldLoad);
  return <LinkedInFlowContext.Provider value={value}>{children}</LinkedInFlowContext.Provider>;
};

/**
 * Prozessstatus aus dem Provider; ohne Provider (isolierte Seite, Test)
 * verwaltet der Hook eigenen Zustand und laedt beim ersten Rendern --
 * ebenfalls nur mit linkedin.lesen.
 */
export function useLinkedInFlow(): LinkedInFlowValue {
  const ctx = useContext(LinkedInFlowContext);
  const { darf } = useBerechtigung();
  const standalone = useFlowState(loadFlowData, ctx === null && darf('linkedin.lesen'));
  return ctx ?? standalone;
}
